// FROM: https://github.com/modusign/modusign-client/blob/1f2cc00e317b3cc6ddf42ace319c72036470a423/app/src/components/UniversalImageField/index.tsx

import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, Flex, Text } from '@modusign/design-system';
import cx from 'classnames';
import { Component, ReactNode } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';

import CenterLoadingSpinner from '@app/components/CenterLoadingSpinner';
import ContainedImage from '@app/components/UniversalImageField/ContainedImage';
import ImageDropZone from '@app/components/UniversalImageField/ImageDropZone';
import { FieldBase } from '@app/features/apply-react-signing/components/FieldRenderer/components/FieldBase';
import { PointDelta } from '@app/features/apply-react-signing/components/FieldRenderer/libs/PointDelta';
import {
  AbsolutePoint,
  AbsoluteSize,
} from '@app/features/apply-react-signing/components/FieldRenderer/types';

import './UniversalImageField.scss';

interface UniversalImageFieldProps {
  boxClassName: string;
  children?: ReactNode;
  draggable?: boolean;

  imageSrc?: string;

  point: AbsolutePoint;
  size: AbsoluteSize;

  /**
   * FIXME: isError, isLoading 및 '이미지 선택 완료' 등 필드의 상태에 대한 인터페이스 설계 개선하기
   */
  isError?: boolean;
  isImageChangeable?: boolean;
  isLoading?: boolean;
  resizable?: boolean;
  rootClassName: string;

  viewport: AbsoluteSize;
  onSizeChange: (size: AbsoluteSize) => void;
  onPointChange: (point: AbsolutePoint) => void;
  onImageChange: (image: { imageSrc: string; fileName: string }) => void;

  onBlur: () => void;
  onClick: () => void;
  onFocus: () => void;
  onOutsideClick: () => void;
}

interface UniversalImageFieldState {
  isImageSelectionDisabled: boolean;
  isFocusing: boolean;
  showGuideline: boolean;
}

class UniversalImageField extends Component<UniversalImageFieldProps, UniversalImageFieldState> {
  private pointDelta: PointDelta;

  constructor(props: UniversalImageFieldProps) {
    super(props);
    this.state = {
      isImageSelectionDisabled: false,
      isFocusing: false,
      showGuideline: false,
    };

    this.pointDelta = new PointDelta();

    this.getMaxConstraints = this.getMaxConstraints.bind(this);
    this.handleDragStart = this.handleDragStart.bind(this);
    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragStop = this.handleDragStop.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.handleResizeStart = this.handleResizeStart.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.handleResizeStop = this.handleResizeStop.bind(this);
  }

  handleDragStart(point: AbsolutePoint) {
    this.pointDelta.initializeStartPoint(point);
    this.setState({ showGuideline: true });
  }

  handleDrag(newPoint: AbsolutePoint) {
    const { onPointChange } = this.props;
    if (this.pointDelta.isExceedThreshold(newPoint)) {
      onPointChange(newPoint);
    }
  }

  handleDragStop(stoppedPoint: AbsolutePoint) {
    /**
     * 드래그하면서 클릭 이벤트가 발생하여 파일 선택 창이 열리는걸
     * 방지하기 위해 좌표의 이동절댓값이 기준을 넘긴 경우 drag로 판단해 button을 disabled 상태로 변경합니다.
     */
    this.setState({
      isImageSelectionDisabled: this.pointDelta.isExceedThreshold(stoppedPoint),
      showGuideline: false,
    });
  }

  handleClick() {
    const { onClick } = this.props;
    this.handleFocus();
    onClick();
  }

  handleFocus() {
    const { onFocus } = this.props;
    this.setState({ isFocusing: true });
    onFocus();
  }

  handleBlur() {
    const { onBlur } = this.props;
    this.setState({ isFocusing: false });
    onBlur();
  }

  handleResizeStart() {
    this.setState({ showGuideline: true });
  }

  handleResize(size: AbsoluteSize) {
    const { onSizeChange } = this.props;
    onSizeChange(size);
  }

  handleResizeStop() {
    this.setState({ showGuideline: false });
  }

  getMaxConstraints(): [number, number] | undefined {
    const {
      point: { x, y },
      viewport: { width, height },
    } = this.props;

    return [width - x, height - y];
  }

  render() {
    const {
      boxClassName,
      children,
      draggable,
      isError,
      isImageChangeable,
      isLoading,
      resizable,
      rootClassName,
      size,
      point,
      imageSrc,
      viewport,
      onImageChange,
      onOutsideClick,
    } = this.props;
    const { height: lastHeight, width: lastWidth } = size;
    const { isImageSelectionDisabled, isFocusing, showGuideline } = this.state;

    const hideText = lastHeight <= 128 || lastWidth <= 128;

    return (
      <FieldBase
        defaultClassName={cx({
          UniversalImageField: true,
          'UniversalImageField--focusing': isFocusing,
          [rootClassName]: rootClassName,
        })}
        disabled={!draggable}
        enableUserSelectHack={false}
        fieldGuideline={{
          guideLineOffset: {
            left: 3,
            top: 2,
            right: 1,
            bottom: 1,
          },
          viewport,
          showGuideLine: showGuideline,
        }}
        maxConstraints={this.getMaxConstraints()}
        minConstraints={[64, 64]}
        point={point}
        readOnly={!resizable}
        size={size}
        onPointChange={this.handleDrag}
        onPointChangeStart={this.handleDragStart}
        onPointChangeStop={this.handleDragStop}
        onSizeChange={this.handleResize}
        onSizeChangeStart={this.handleResizeStart}
        onSizeChangeStop={this.handleResizeStop}
      >
        <Box height="100%" lineHeight={0} width="100%">
          <OutsideClickHandler
            onOutsideClick={() => {
              this.handleBlur();
              onOutsideClick();
            }}
          >
            <Box height="100%" position="absolute" width="100%">
              <Flex
                alignItems="center"
                className={cx('UniversalImageField__box', boxClassName)}
                height="100%"
                justifyContent="center"
                sx={{
                  backgroundColor: isError
                    ? 'boxBackgroundNegativeBase'
                    : 'rgba(255, 255, 255, 0.5)',
                  borderColor: isError ? 'pureRed600' : '#9e9e9e',
                  borderRadius: '2px',
                  borderWidth: '1px',
                  borderStyle: isError || imageSrc ? 'dashed' : 'solid',
                  position: 'relative',
                }}
                width="100%"
              >
                <ImageFieldContent
                  hideText={hideText}
                  imageSrc={imageSrc}
                  isError={isError}
                  isImageChangeable={!!isImageChangeable}
                  isImageSelectionDisabled={isImageSelectionDisabled}
                  isLoading={isLoading}
                  size={size}
                  onBlur={this.handleBlur}
                  onChange={onImageChange}
                  onClick={this.handleClick}
                  onFocus={this.handleFocus}
                />
              </Flex>
            </Box>
            {children}
          </OutsideClickHandler>
        </Box>
      </FieldBase>
    );
  }
}

function ImageFieldContent({
  hideText,
  isError,
  isImageChangeable,
  isImageSelectionDisabled,
  isLoading,
  size,
  imageSrc,
  onBlur,
  onChange,
  onClick,
  onFocus,
}: {
  hideText: boolean;
  isError?: boolean;
  isImageChangeable: boolean;
  isImageSelectionDisabled: boolean;
  isLoading?: boolean;
  size: AbsoluteSize;
  imageSrc: string | undefined;
  onBlur: () => void;
  onChange: (image: { imageSrc: string; fileName: string }) => void;
  onClick: () => void;
  onFocus: () => void;
}) {
  if (isLoading) {
    return <CenterLoadingSpinner color="gray" size={36} />;
  }

  if (isError) {
    return (
      <>
        <Box as="span" color="iconNegativeBase" display="inline-block" mb={!hideText ? 4 : 0}>
          <FontAwesomeIcon
            icon={faExclamationTriangle}
            style={{
              fontWeight: 100,
              height: 28,
              width: 28,
            }}
          />
        </Box>
        {!hideText && (
          <Text color="textNegativeBase" fontSize="sm">
            이미지를 불러올 수 없습니다.
          </Text>
        )}
      </>
    );
  }

  if (imageSrc) {
    return (
      <Box height="100%" width="100%" onClick={onClick}>
        <ContainedImage containerSize={size} src={imageSrc} />
      </Box>
    );
  }

  return (
    <ImageDropZone
      changeable={isImageChangeable}
      disabled={isImageSelectionDisabled}
      hidePlaceholderText={hideText}
      onBlur={onBlur}
      onChange={onChange}
      onClick={onClick}
      onFocus={onFocus}
    />
  );
}

export default UniversalImageField;
